import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { SEQUENCING_DECKS } from '../data/sequencing';
import { useTranslation } from '../i18n/useTranslation';
import '../styles/sequencing.css';

// ── Helpers ───────────────────────────────────────────────────────────────────

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function makeInitialState(deck) {
  const steps = (deck?.steps || []).map(s => ({
    ...s,
    bgColor: s.bgColor || deck?.color || '#EEF2FF',
  }));
  return {
    pool:  shuffle(steps),
    slots: Array(steps.length).fill(null),
  };
}

// ── Card ──────────────────────────────────────────────────────────────────────

const SeqCard = React.memo(function SeqCard({ step, showLabel, cardState, dragHandlers }) {
  const { tc } = useTranslation();
  const [varIdx, setVarIdx] = useState(0);

  const images = step.images ?? [step.imageUrl ?? step.image].filter(Boolean);
  const hasVariations = images.length > 1;

  const cycleVar = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setVarIdx(i => (i + 1) % images.length);
  };

  // Each entry in images is either a plain string URL or a sprite descriptor object
  // { src, col, row, cols, rows }  ← sprite sheet panel
  const renderImage = (entry) => {
    if (!entry) return null;
    if (typeof entry === 'string') {
      return <img src={entry} alt={tc(step.label)} className="seq-card-img" draggable={false} />;
    }
    if (entry.col !== undefined) {
      const { src, col, row, cols = 6, rows = 1 } = entry;
      const xPct = cols <= 1 ? '0%' : `${(col / (cols - 1)) * 100}%`;
      const yPct = rows <= 1 ? '0%' : `${(row / (rows - 1)) * 100}%`;
      return (
        <div
          className="seq-card-sprite"
          style={{
            backgroundImage:    `url(${src})`,
            backgroundSize:     `${cols * 100}% ${rows * 100}%`,
            backgroundPosition: `${xPct} ${yPct}`,
          }}
        />
      );
    }
    return <img src={entry.src} alt={tc(step.label)} className="seq-card-img" draggable={false} />;
  };

  const currentEntry  = images[varIdx] ?? null;
  const isCartoon     = typeof currentEntry === 'object' && currentEntry?.col !== undefined;
  const varTag        = hasVariations ? (isCartoon ? 'cartoon' : 'real life') : null;
  const showImage     = !!currentEntry;

  return (
    <div
      className={[
        'seq-card',
        cardState === 'correct' ? 'seq-card--correct' : '',
        cardState === 'wrong'   ? 'seq-card--wrong'   : '',
      ].filter(Boolean).join(' ')}
      {...dragHandlers}
    >
      <div className="seq-card-img-wrap">
        {showImage
          ? renderImage(currentEntry)
          : <div className="seq-card-emoji-bg" style={{ background: step.bgColor || '#F0F4FF' }}>
              <span className="seq-card-emoji">{step.emoji}</span>
            </div>
        }
        {cardState === 'correct' && <div className="seq-card-state-badge seq-card-state-badge--correct">✓</div>}
        {cardState === 'wrong'   && <div className="seq-card-state-badge seq-card-state-badge--wrong">✗</div>}

        {hasVariations && (
          <button
            className="seq-card-var-btn"
            onClick={cycleVar}
            onTouchStart={e => e.stopPropagation()}
            onTouchEnd={cycleVar}
            aria-label="Switch image variation"
          >
            {images.map((_, i) => (
              <span key={i} className={`seq-card-var-dot${i === varIdx ? ' seq-card-var-dot--on' : ''}`} />
            ))}
          </button>
        )}
      </div>
      {showLabel && (
        <div className="seq-card-label-row">
          {varTag && <span className="seq-card-var-tag">{varTag}</span>}
          <div className="seq-card-label">{tc(step.label)}</div>
        </div>
      )}
    </div>
  );
});

// ── Main page ─────────────────────────────────────────────────────────────────

export const SequencingPage = ({ generated = false, userSource = false }) => {
  const { deckId } = useParams();
  const navigate   = useNavigate();
  const { t, tc } = useTranslation();

  const fromFirestore = generated || userSource;

  // For static decks resolve immediately; for generated/user decks fetch from Firestore
  const staticDeck = fromFirestore ? null : SEQUENCING_DECKS.find(d => d.id === deckId);
  const [deck, setDeck]         = useState(staticDeck);
  const [deckLoading, setDeckLoading] = useState(fromFirestore);

  useEffect(() => {
    if (!fromFirestore) return;
    const collectionName = userSource ? 'userSequencingDecks' : 'generatedSequencingDecks';
    getDoc(doc(db, collectionName, deckId)).then(snap => {
      if (snap.exists()) setDeck({ id: snap.id, ...snap.data() });
      setDeckLoading(false);
    });
  }, [deckId, fromFirestore, userSource]);

  // `useState(staticDeck)` only sets `deck` on first mount — if `deckId` ever
  // changes while this component instance stays mounted (React Router doesn't
  // remount on param-only changes), `deck` would otherwise keep showing the
  // previous static deck under the new URL. Re-sync it explicitly.
  useEffect(() => {
    if (fromFirestore) return;
    setDeck(SEQUENCING_DECKS.find(d => d.id === deckId));
  }, [deckId, fromFirestore]);

  // Decks with `levels` need a difficulty picked before there's a step list to play
  const [selectedLevel, setSelectedLevel] = useState(null);
  useEffect(() => { setSelectedLevel(null); }, [deckId]);

  const activeDeck = deck?.levels
    ? { ...deck, steps: selectedLevel ? deck.levels[selectedLevel].steps : [] }
    : deck;

  const [activity, setActivity]   = useState(() => makeInitialState(activeDeck));
  const [phase, setPhase]         = useState('play'); // 'play' | 'feedback' | 'complete'
  const [correctSet, setCorrectSet] = useState(new Set()); // slot indices that are correct
  const [showLabels, setShowLabels] = useState(true);
  const [celebrating, setCelebrating] = useState(false);
  const [dragOver, setDragOver]   = useState(null); // { type: 'pool'|'slot', index }
  const [customOrder,  setCustomOrder]  = useState(null);
  const [settingOrder, setSettingOrder] = useState(false);
  const [setupMoved,   setSetupMoved]   = useState(false);
  const [feedbackMode, setFeedbackMode] = useState(
    () => localStorage.getItem('sc-seq-feedback') || 'submit'
  );
  const settingOrderRef = useRef(false);
  useEffect(() => { settingOrderRef.current = settingOrder; }, [settingOrder]);

  const { pool, slots } = activity;
  const allFilled = pool.length === 0 && slots.every(Boolean);

  // Auto-check when all slots filled in immediate-feedback mode
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (feedbackMode !== 'immediate') return;
    if (!allFilled || phase !== 'play' || settingOrderRef.current) return;
    checkAnswer();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity, feedbackMode, phase]);

  // ── Move engine ─────────────────────────────────────────────────────────────
  // Handles all drag transfers: pool→slot, slot→slot (swap), slot→pool

  const applyMove = useCallback((src, tgt) => {
    if (!src || !tgt) return;
    if (src.type === tgt.type && src.index === tgt.index) return;

    setActivity(prev => {
      const newPool  = [...prev.pool];
      const newSlots = [...prev.slots];

      let card;
      if (src.type === 'pool') {
        card = newPool[src.index];
        if (!card) return prev;
        newPool.splice(src.index, 1);
      } else {
        card = newSlots[src.index];
        if (!card) return prev;
        newSlots[src.index] = null;
      }

      if (tgt.type === 'pool') {
        newPool.push(card);
      } else {
        const displaced = newSlots[tgt.index];
        newSlots[tgt.index] = card;
        if (displaced) {
          if (src.type === 'slot') {
            newSlots[src.index] = displaced; // swap between slots
          } else {
            newPool.push(displaced); // push displaced back to pool
          }
        }
      }

      return { pool: newPool, slots: newSlots };
    });

    // Exit feedback mode whenever the user adjusts anything
    setPhase(p => (p === 'feedback' ? 'play' : p));
    setCorrectSet(new Set());
    if (settingOrderRef.current) setSetupMoved(true);
  }, []);

  // ── Desktop HTML5 drag ──────────────────────────────────────────────────────

  const desktopSrc  = useRef(null);
  const desktopSrcEl = useRef(null);

  const onDragStart = (e, src) => {
    desktopSrc.current   = src;
    desktopSrcEl.current = e.currentTarget;
    e.dataTransfer.effectAllowed = 'move';
    requestAnimationFrame(() => {
      if (desktopSrcEl.current) desktopSrcEl.current.style.opacity = '0.3';
    });
  };

  const onDragOver = (e, tgt) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOver(tgt);
  };

  const onDrop = (e, tgt) => {
    e.preventDefault();
    setDragOver(null);
    applyMove(desktopSrc.current, tgt);
    desktopSrc.current = null;
    if (desktopSrcEl.current) { desktopSrcEl.current.style.opacity = ''; desktopSrcEl.current = null; }
  };

  const onDragEnd = () => {
    setDragOver(null);
    desktopSrc.current = null;
    if (desktopSrcEl.current) { desktopSrcEl.current.style.opacity = ''; desktopSrcEl.current = null; }
  };

  // ── Touch drag ──────────────────────────────────────────────────────────────

  const ghost       = useRef(null);
  const ghostOffset = useRef({ x: 0, y: 0 });
  const touchSrc    = useRef(null);
  const touchTarget = useRef(null);
  const touchSrcEl  = useRef(null);
  const touchMoveRef = useRef(null);
  const touchEndRef  = useRef(null);

  const onTouchStart = (e, src) => {
    if (phase === 'complete') return;
    e.preventDefault();
    const t = e.touches[0];
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();

    touchSrc.current   = src;
    touchSrcEl.current = el;
    touchTarget.current = null;
    el.style.opacity = '0.2';

    // Create ghost clone
    ghostOffset.current = { x: t.clientX - rect.left, y: t.clientY - rect.top };
    const g = el.cloneNode(true);
    Object.assign(g.style, {
      position:      'fixed',
      left:          rect.left + 'px',
      top:           rect.top  + 'px',
      width:         rect.width + 'px',
      height:        rect.height + 'px',
      pointerEvents: 'none',
      zIndex:        '9999',
      opacity:       '0.93',
      transform:     'scale(1.07) rotate(2deg)',
      boxShadow:     '0 24px 56px rgba(0,0,0,0.32)',
      borderRadius:  '16px',
      transition:    'none',
    });
    document.body.appendChild(g);
    ghost.current = g;

    touchMoveRef.current = (ev) => {
      ev.preventDefault();
      const touch = ev.touches[0];
      if (ghost.current) {
        ghost.current.style.left = (touch.clientX - ghostOffset.current.x) + 'px';
        ghost.current.style.top  = (touch.clientY - ghostOffset.current.y) + 'px';
        ghost.current.style.pointerEvents = 'none';
      }
      const under = document.elementFromPoint(touch.clientX, touch.clientY);
      if (!under) { touchTarget.current = null; setDragOver(null); return; }
      const slotEl = under.closest('[data-slot]');
      const poolEl = under.closest('[data-pool]');
      if (slotEl) {
        const tgt = { type: 'slot', index: parseInt(slotEl.dataset.slot, 10) };
        touchTarget.current = tgt;
        setDragOver(tgt);
      } else if (poolEl) {
        const tgt = { type: 'pool', index: -1 };
        touchTarget.current = tgt;
        setDragOver(tgt);
      } else {
        touchTarget.current = null;
        setDragOver(null);
      }
    };

    touchEndRef.current = () => {
      if (ghost.current)      { ghost.current.remove();      ghost.current = null; }
      if (touchSrcEl.current) { touchSrcEl.current.style.opacity = ''; touchSrcEl.current = null; }
      document.removeEventListener('touchmove', touchMoveRef.current);
      document.removeEventListener('touchend',  touchEndRef.current);
      applyMove(touchSrc.current, touchTarget.current);
      touchSrc.current    = null;
      touchTarget.current = null;
      setDragOver(null);
    };

    document.addEventListener('touchmove', touchMoveRef.current, { passive: false });
    document.addEventListener('touchend',  touchEndRef.current,  { once: true });
  };

  // ── Game logic ──────────────────────────────────────────────────────────────

  const checkAnswer = () => {
    if (!allFilled) return;
    const correct = new Set();
    if (customOrder) {
      slots.forEach((s, i) => { if (s && customOrder[i] === s.id) correct.add(i); });
    } else {
      slots.forEach((s, i) => {
        if (!s) return;
        const slotNum = i + 1;
        const ok = s.acceptableOrders ? s.acceptableOrders.includes(slotNum) : s.order === slotNum;
        if (ok) correct.add(i);
      });
    }
    setCorrectSet(correct);
    if (correct.size === slots.length) {
      setPhase('complete');
      setCelebrating(true);
      setTimeout(() => setCelebrating(false), 3600);
    } else {
      setPhase('feedback');
    }
  };

  const resetAll = () => {
    setActivity(makeInitialState(activeDeck));
    setPhase('play');
    setCorrectSet(new Set());
    setCelebrating(false);
    setSettingOrder(false);
  };

  const changeLevel = () => setSelectedLevel(null);

  const shufflePool = () => {
    setActivity(prev => ({ ...prev, pool: shuffle([...prev.pool]) }));
  };

  const enterSetupMode = () => {
    const steps = (activeDeck?.steps || [])
      .slice()
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map(s => ({ ...s, bgColor: s.bgColor || activeDeck?.color || '#EEF2FF' }));
    setActivity({ pool: [], slots: steps });
    setSettingOrder(true);
    setSetupMoved(false);
    setPhase('play');
    setCorrectSet(new Set());
  };

  const saveCustomOrder = () => {
    setCustomOrder(slots.map(s => s?.id ?? null));
    setSettingOrder(false);
    setActivity(makeInitialState(activeDeck));
    setPhase('play');
    setCorrectSet(new Set());
  };

  const cancelSetupMode = () => {
    setSettingOrder(false);
    setActivity(makeInitialState(activeDeck));
    setPhase('play');
    setCorrectSet(new Set());
  };

  // ── Render helpers ──────────────────────────────────────────────────────────

  const slotCardState = (i) => {
    if (phase === 'complete') return 'correct';
    if (phase === 'feedback') return correctSet.has(i) ? 'correct' : 'wrong';
    return 'idle';
  };

  const poolDragHandlers = (i) => ({
    draggable:    true,
    onDragStart:  (e) => onDragStart(e, { type: 'pool', index: i }),
    onDragEnd:    onDragEnd,
    onTouchStart: (e) => onTouchStart(e, { type: 'pool', index: i }),
  });

  const slotDragHandlers = (i) => ({
    draggable:    phase !== 'complete',
    onDragStart:  (e) => onDragStart(e, { type: 'slot', index: i }),
    onDragEnd:    onDragEnd,
    onTouchStart: (e) => onTouchStart(e, { type: 'slot', index: i }),
  });

  // Re-initialize game state when a generated/user deck loads from Firestore,
  // or when a level is picked/changed for a deck with multiple levels
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (deck && fromFirestore) setActivity(makeInitialState(deck));
    if (deck?.levels && selectedLevel) setActivity(makeInitialState(activeDeck));
  // eslint-disable-next-line react-hooks/exhaustive-deps
    setCustomOrder(null);
    setSettingOrder(false);
  }, [deck?.id, selectedLevel]);

  // ── Not found / loading ──────────────────────────────────────────────────

  if (deckLoading) {
    return (
      <div className="seq-page seq-page--center">
        <p style={{ color: 'var(--text-muted)' }}>{t('seq.loading')}</p>
      </div>
    );
  }

  if (!deck) {
    return (
      <div className="seq-page seq-page--center">
        <p style={{ color: 'var(--text-muted)', marginBottom: 16 }}>{t('seq.notFound')}</p>
        <button className="seq-back-btn" onClick={() => navigate('/sequencing')}>← {t('seq.back')}</button>
      </div>
    );
  }

  // ── Level picker (decks with multiple difficulty levels) ─────────────────

  if (deck.levels && !selectedLevel) {
    const LEVEL_INFO = {
      beginner:     { labelKey: 'difficulty.beginner',     emoji: '🌱' },
      intermediate: { labelKey: 'difficulty.intermediate', emoji: '📈' },
      advanced:     { labelKey: 'difficulty.advanced',     emoji: '⭐' },
    };
    return (
      <div className="seq-page seq-page--center">
        <button className="seq-back-btn" onClick={() => navigate('/sequencing')} style={{ alignSelf: 'flex-start' }}>
          ← {t('seq.back')}
        </button>
        <h1 className="seq-title" style={{ marginTop: 12 }}>{tc(deck.title)}</h1>
        <p className="seq-subtitle" style={{ marginBottom: 24 }}>{t('seq.chooseDifficulty')}</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%', maxWidth: 360 }}>
          {Object.entries(deck.levels).map(([level, { steps }]) => {
            const info = LEVEL_INFO[level] || { labelKey: null, emoji: '▶' };
            return (
              <button
                key={level}
                onClick={() => setSelectedLevel(level)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '14px 18px', borderRadius: 14,
                  border: `1.5px solid ${deck.accentColor}33`,
                  background: '#fff', cursor: 'pointer', textAlign: 'left',
                  fontFamily: 'inherit',
                }}
              >
                <span style={{ fontSize: 24 }}>{info.emoji}</span>
                <span style={{ flex: 1 }}>
                  <span style={{ display: 'block', fontSize: 15, fontWeight: 700, color: '#111827' }}>{info.labelKey ? t(info.labelKey) : level}</span>
                  <span style={{ display: 'block', fontSize: 12, color: '#6B7280' }}>{steps.length} {t('seq.steps')}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="seq-page">

      {/* ── Celebration ── */}
      {celebrating && (
        <div className="seq-celebrate" aria-live="polite" aria-atomic="true">
          <div className="seq-celebrate-card">
            <div className="seq-celebrate-emoji">🎉</div>
            <h2 className="seq-celebrate-title">{t('seq.amazingJob')}</h2>
            <p className="seq-celebrate-body">
              {t('seq.youPutAll')} {slots.length} {t('seq.stepsRightOrder')}
            </p>
          </div>
        </div>
      )}

      {/* ── Header ── */}
      <header className="seq-header">
        <button
          className="seq-back-btn"
          onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/sequencing')}
          aria-label={t('seq.goBack')}
        >
          ← {t('seq.back')}
        </button>

        <div className="seq-header-mid">
          <h1 className="seq-title">{tc(deck.title)}</h1>
          <p className="seq-subtitle">
            {t('seq.dragInstructions')}
            {deck.levels && (
              <>
                {' · '}
                <button
                  onClick={changeLevel}
                  style={{
                    border: 'none', background: 'none', padding: 0, font: 'inherit',
                    color: deck.accentColor, textDecoration: 'underline', cursor: 'pointer',
                  }}
                >
                  {t(`difficulty.${selectedLevel}`)} · {t('seq.changeLevel')}
                </button>
              </>
            )}
          </p>
        </div>

        {/* Custom order pill + setup button */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {customOrder && !settingOrder && (
            <span style={{
              display: 'flex', alignItems: 'center', gap: 4,
              fontSize: 11, fontWeight: 700, color: '#065F46',
              background: '#D1FAE5', border: '1.5px solid #6EE7B7',
              borderRadius: 20, padding: '3px 10px',
            }}>
              Custom ✓
              <button
                onClick={() => setCustomOrder(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 0 0 4px', lineHeight: 1, fontSize: 13, color: '#065F46' }}
                title="Clear custom order"
              >×</button>
            </span>
          )}
          {!settingOrder && (
            <button
              onClick={enterSetupMode}
              title="Set a custom correct order"
              style={{
                background: 'none', border: '1.5px solid var(--border)',
                borderRadius: 8, padding: '5px 8px', cursor: 'pointer',
                fontSize: 14, lineHeight: 1, color: 'var(--text-secondary)',
              }}
            >✏️</button>
          )}
        </div>

        {/* Show Labels toggle */}
        <button
          className={`seq-toggle-btn ${showLabels ? 'seq-toggle-btn--on' : ''}`}
          onClick={() => setShowLabels(p => !p)}
          aria-pressed={showLabels}
          title={showLabels ? t('seq.hideLabels') : t('seq.showLabels')}
        >
          <span className="seq-toggle-label-text">{t('seq.labels')}</span>
          <div className="seq-toggle-pill">
            <div className="seq-toggle-thumb" />
          </div>
        </button>

        {/* Auto-check toggle */}
        <button
          className={`seq-toggle-btn ${feedbackMode === 'immediate' ? 'seq-toggle-btn--on' : ''}`}
          onClick={() => {
            const next = feedbackMode === 'immediate' ? 'submit' : 'immediate';
            setFeedbackMode(next);
            localStorage.setItem('sc-seq-feedback', next);
          }}
          aria-pressed={feedbackMode === 'immediate'}
          title={feedbackMode === 'immediate' ? 'Switch to manual check' : 'Auto-check when all cards placed'}
        >
          <span className="seq-toggle-label-text">Auto-check</span>
          <div className="seq-toggle-pill">
            <div className="seq-toggle-thumb" />
          </div>
        </button>
      </header>

      {/* ── Setup mode banner ── */}
      {settingOrder && (
        <div style={{
          background: '#FEF3C7', borderBottom: '2px solid #FCD34D',
          padding: '10px 20px', fontSize: 13, fontWeight: 600, color: '#92400E',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          ✏️ Setup mode — drag the cards into your preferred correct order, then tap Save.
        </div>
      )}

      {/* ── Feedback bar ── */}
      {phase === 'feedback' && (
        <div className="seq-feedback-bar" role="alert">
          <span className="seq-feedback-score">
            {correctSet.size}/{slots.length} correct — {Math.round((correctSet.size / slots.length) * 100)}%
          </span>
          <span className="seq-feedback-hint">{t('seq.inRightPlace')}</span>
        </div>
      )}
      {phase === 'complete' && (
        <div className="seq-feedback-bar seq-feedback-bar--correct" role="alert">
          🎉 {slots.length}/{slots.length} correct — 100% · {t('seq.stepsCorrectOrder')}
        </div>
      )}

      {/* ── SECTION 1: Available Cards pool ── */}
      <section className="seq-section">
        <div className="seq-section-hd">
          <span className="seq-section-label">{t('seq.availableCards')}</span>
          {pool.length === 0 && (
            <span className="seq-section-done">✓ {t('seq.allPlacedShort')}</span>
          )}
        </div>

        <div
          className={`seq-pool ${dragOver?.type === 'pool' ? 'seq-pool--over' : ''}`}
          data-pool="true"
          onDragOver={e => onDragOver(e, { type: 'pool', index: -1 })}
          onDrop={e => onDrop(e, { type: 'pool', index: -1 })}
          onDragLeave={() => setDragOver(null)}
        >
          {pool.length === 0 ? (
            <div className="seq-pool-empty-state">
              <span className="seq-pool-empty-icon">✓</span>
              <span>{t('seq.allPlaced')}</span>
            </div>
          ) : (
            <div className="seq-pool-cards">
              {pool.map((step, i) => (
                <SeqCard
                  key={step.id}
                  step={step}
                  showLabel={showLabels}
                  cardState="idle"
                  dragHandlers={poolDragHandlers(i)}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── SECTION 2: Numbered drop slots ── */}
      <section className="seq-section seq-section--slots">
        <div className="seq-section-hd">
          <span className="seq-section-label">{t('seq.yourOrder')}</span>
        </div>

        <div className="seq-slots">
          <div className="seq-slots-inner">
          {slots.map((step, i) => {
            const isOver    = dragOver?.type === 'slot' && dragOver.index === i;
            const cardState = step ? slotCardState(i) : 'idle';

            return (
              <div
                key={i}
                className={[
                  'seq-slot',
                  step       ? 'seq-slot--filled' : 'seq-slot--empty',
                  isOver     ? 'seq-slot--over'   : '',
                  cardState === 'correct' ? 'seq-slot--correct' : '',
                  cardState === 'wrong'   ? 'seq-slot--wrong'   : '',
                ].filter(Boolean).join(' ')}
                data-slot={i}
                onDragOver={e => onDragOver(e, { type: 'slot', index: i })}
                onDrop={e => onDrop(e, { type: 'slot', index: i })}
                onDragLeave={() => setDragOver(null)}
              >
                <div className="seq-slot-num">{i + 1}</div>

                {step ? (
                  <SeqCard
                    step={step}
                    showLabel={showLabels}
                    cardState={cardState}
                    dragHandlers={slotDragHandlers(i)}
                  />
                ) : (
                  <div className="seq-slot-drop-hint">
                    <div className="seq-slot-drop-icon">⬚</div>
                    <div className="seq-slot-drop-text">{t('seq.dropHere')}</div>
                  </div>
                )}
              </div>
            );
          })}
          </div>
        </div>
      </section>

      {/* ── Action bar ── */}
      <div className="seq-action-bar">
        {settingOrder ? (
          <>
            <button className="seq-btn seq-btn--ghost" onClick={cancelSetupMode}>
              Cancel
            </button>
            <button
              className={`seq-btn seq-btn--primary ${(!allFilled || !setupMoved) ? 'seq-btn--muted' : ''}`}
              onClick={saveCustomOrder}
              disabled={!allFilled || !setupMoved}
              title={!allFilled ? 'Place all cards in slots first' : !setupMoved ? 'Drag at least one card to set your order' : 'Save this as the correct order'}
            >
              Save as Correct Order
            </button>
          </>
        ) : phase === 'complete' ? (
          <>
            <button className="seq-btn seq-btn--ghost" onClick={resetAll}>
              {t('seq.playAgain')}
            </button>
            <button
              className="seq-btn seq-btn--primary"
              onClick={() => navigate('/sequencing')}
            >
              {t('seq.moreActivities')}
            </button>
          </>
        ) : (
          <>
            <button className="seq-btn seq-btn--ghost" onClick={shufflePool}>
              ⟳ {t('seq.shuffle')}
            </button>
            <button className="seq-btn seq-btn--ghost" onClick={resetAll}>
              {t('seq.reset')}
            </button>
            {feedbackMode === 'submit' && (
              <button
                className={`seq-btn seq-btn--primary ${!allFilled ? 'seq-btn--muted' : ''}`}
                onClick={checkAnswer}
                disabled={!allFilled}
                title={!allFilled ? t('seq.placeAllFirst') : t('seq.checkYourAnswer')}
              >
                ✓ {t('seq.checkAnswer')}
              </button>
            )}
          </>
        )}
      </div>

    </div>
  );
};
